import { REVENUE_LEAKS } from '../utils/scoring';

export default function RevenueLeakMap({ leaks, onChange }) {
  const totalLeak = Object.values(leaks).reduce(
    (sum, l) => sum + (l.active ? l.estimatedImpact : 0), 0
  );

  const handleToggle = (id) => {
    onChange({
      ...leaks,
      [id]: { ...leaks[id], active: !leaks[id].active },
    });
  };

  const handleImpact = (id, val) => {
    onChange({
      ...leaks,
      [id]: { ...leaks[id], estimatedImpact: parseInt(val) || 0 },
    });
  };

  const handleNotes = (id, val) => {
    onChange({
      ...leaks,
      [id]: { ...leaks[id], notes: val },
    });
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-bold text-white text-lg">Revenue Leak Map</h3>
          <p className="text-xs text-navy-400 mt-0.5">Identify where revenue is leaving the building</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-navy-400">Est. Annual Leakage</p>
          <p className="text-2xl font-black text-red-400">
            ${totalLeak.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid gap-3">
        {/* Standard leak categories */}
        {REVENUE_LEAKS.map((leak) => {
          const data = leaks[leak.id] || { active: false, estimatedImpact: leak.defaultImpact, notes: '' };
          return (
            <div
              key={leak.id}
              className={`rounded-lg border p-4 transition-all cursor-pointer ${
                data.active
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-neutral-800/30 border-neutral-700/30 hover:border-neutral-600/50'
              }`}
            >
              <div className="flex items-center gap-3" onClick={() => handleToggle(leak.id)}>
                <span className="text-xl">{leak.icon}</span>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${data.active ? 'text-red-400' : 'text-neutral-300'}`}>
                    {leak.name}
                  </p>
                  {data.active && data.notes && <p className="text-xs text-neutral-500 mt-0.5">{data.notes}</p>}
                </div>
                <div className={`w-10 h-5 rounded-full p-0.5 transition-all ${data.active ? 'bg-red-500' : 'bg-neutral-700'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${data.active ? 'translate-x-5' : ''}`} />
                </div>
              </div>

              {data.active && (
                <div className="mt-3 pl-9 space-y-2 animate-fade-in-up">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">Est. Impact: $</span>
                    <input
                      type="number"
                      value={data.estimatedImpact}
                      onChange={(e) => handleImpact(leak.id, e.target.value)}
                      className="input-field w-32 text-sm py-1.5 text-red-400"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="text-xs text-neutral-500">/year</span>
                  </div>
                  <textarea
                    value={data.notes}
                    onChange={(e) => handleNotes(leak.id, e.target.value)}
                    placeholder="Notes on this leak..."
                    rows={2}
                    className="input-field text-xs py-1.5"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* AI-discovered custom leaks */}
        {Object.entries(leaks).filter(([id]) => id.startsWith('ai_')).map(([id, data]) => (
          <div
            key={id}
            className={`rounded-lg border p-4 transition-all cursor-pointer ${
              data.active
                ? 'bg-red-500/10 border-red-500/30'
                : 'bg-neutral-800/30 border-neutral-700/30'
            }`}
          >
            <div className="flex items-center gap-3" onClick={() => handleToggle(id)}>
              <span className="text-xl">{data.customIcon || '🔍'}</span>
              <div className="flex-1">
                <p className={`text-sm font-medium ${data.active ? 'text-red-400' : 'text-neutral-300'}`}>
                  {data.customName || id.replace('ai_', '').replace(/_/g, ' ')}
                </p>
                <p className="text-[10px] text-brand-gold mt-0.5">AI Identified</p>
                {data.active && data.notes && <p className="text-xs text-neutral-500 mt-0.5">{data.notes}</p>}
              </div>
              <div className={`w-10 h-5 rounded-full p-0.5 transition-all ${data.active ? 'bg-red-500' : 'bg-neutral-700'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${data.active ? 'translate-x-5' : ''}`} />
              </div>
            </div>

            {data.active && (
              <div className="mt-3 pl-9 space-y-2 animate-fade-in-up">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-500">Est. Impact: $</span>
                  <input
                    type="number"
                    value={data.estimatedImpact}
                    onChange={(e) => handleImpact(id, e.target.value)}
                    className="input-field w-32 text-sm py-1.5 text-red-400"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-xs text-neutral-500">/year</span>
                </div>
                <textarea
                  value={data.notes}
                  onChange={(e) => handleNotes(id, e.target.value)}
                  placeholder="Notes..."
                  rows={2}
                  className="input-field text-xs py-1.5"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
